// Commercial service landing pages. Each object drives one URL via <ServicePageLayout />.
// Copy is written to be accurate and useful — no invented reviews, ratings, dates, prices,
// or claims of fabrication in-house. Todd works alongside the Superior Signs team; language
// throughout reflects "coordinated" or "helped navigate" where appropriate.

const CENTRAL_VA_CITIES = [
  "Richmond",
  "Henrico",
  "Chesterfield",
  "Midlothian",
  "Glen Allen",
  "Short Pump",
  "Hanover",
  "Ashland",
  "Mechanicsville",
  "Central Virginia",
];

export const SERVICES = {
  "commercial-signs-richmond-va": {
    path: "/commercial-signs-richmond-va",
    seoTitle: "Commercial Signs Richmond VA | Design, Permitting & Installation",
    seoDescription:
      "Commercial signage help for Richmond, VA businesses — building signs, storefront signs, monument signs, permits, surveys, and installation. Talk directly with Todd Friedman.",
    eyebrow: "Commercial Signs · Richmond, VA",
    h1: "Commercial Signs in Richmond, Virginia",
    intro: [
      "If your business needs a new sign, an updated sign, or help fixing one that is not working, the process can move faster and cost less when you have someone local coordinating it.",
      "Todd Friedman helps Richmond-area businesses navigate the full commercial signage process — site surveys, landlord criteria, drawings, permitting, fabrication and installation — as the local customer point of contact, coordinating work through the Superior Signs team.",
    ],
    sections: [
      {
        id: "building-storefront",
        heading: "Building and storefront signs",
        kind: "paragraphs",
        paragraphs: [
          "Wall-mounted signage is often the most important piece of a business's identity. It tells customers where you are, communicates your brand, and — in many cases — has to satisfy landlord criteria, city zoning rules, and electrical codes at the same time.",
          "Todd coordinates the process with the Superior Signs team: understanding the wall, reviewing the landlord's sign criteria, drafting a design that respects both the brand and the code, and moving it through permitting, fabrication and installation.",
        ],
      },
      {
        id: "channel-letters",
        heading: "Channel letters and illuminated signs",
        kind: "paragraphs",
        paragraphs: [
          "Illuminated channel letters are one of the most common exterior sign types for storefronts. They can be internally lit (front-lit, halo-lit, or both), face-lit with LED modules, or mounted on a raceway that houses the wiring.",
          "The right approach depends on the wall, the landlord's electrical provisions, and the look you're going for. Todd walks through the options in plain language before drawings are commissioned.",
        ],
      },
      {
        id: "monument",
        heading: "Monument and roadside signs",
        kind: "paragraphs",
        paragraphs: [
          "Monument signs sit at ground level and are usually the first thing drivers see. They involve masonry, setbacks, utility locations, tenant panels, and sometimes an electronic message center.",
          "New monument signs, refacing an existing sign, and tenant-panel updates are all coordinated through Todd — including survey, drawings, permitting, and installation with the Superior Signs team.",
        ],
      },
      {
        id: "lobby-dimensional",
        heading: "Lobby and dimensional-letter signs",
        kind: "paragraphs",
        paragraphs: [
          "Interior signage — reception walls, dimensional letters, wayfinding — is often the finishing piece of a build-out. Todd coordinates layout, materials and installation with the Superior Signs team so the interior identity matches the exterior brand.",
        ],
      },
      {
        id: "window-graphics",
        heading: "Window graphics",
        kind: "paragraphs",
        paragraphs: [
          "Window graphics can communicate hours, phone numbers, a website, or the brand itself. They are often the fastest and most economical way to make a storefront look finished, and they can be installed while a larger sign package is in permitting.",
        ],
      },
      {
        id: "repair-replace",
        heading: "Sign repair and replacement",
        kind: "paragraphs",
        paragraphs: [
          "Signs fail. LEDs age out, faces crack, letters come loose, cabinets rust. Sometimes a repair makes sense; sometimes replacement is smarter over the long run.",
          "Todd can look at what you have, explain the trade-offs, and coordinate whichever route makes more sense for the property with the Superior Signs team.",
        ],
      },
      {
        id: "surveys-permits-install",
        heading: "Surveys, permits and installation",
        kind: "list",
        items: [
          "Site survey coordination — wall condition, dimensions, electrical availability, mounting surface",
          "Landlord sign-criteria review",
          "Design drawings that meet code and landlord requirements",
          "Permit applications with the appropriate city or county office",
          "Fabrication through the Superior Signs team",
          "Installation, cleanup, and final inspection coordination",
          "Removal and disposal of old signs when needed",
        ],
      },
    ],
    serviceArea: {
      heading: "Where Todd works",
      description:
        "Richmond, Virginia and the surrounding Central Virginia region. Below are the areas most projects fall within.",
      cities: CENTRAL_VA_CITIES,
    },
    faqs: [
      {
        q: "Does Todd design the sign, or just coordinate it?",
        a: "Todd is the local customer point of contact. He walks through the concept and layout with you before drawings are commissioned, then coordinates the finished drawings through permitting and fabrication with the Superior Signs team.",
      },
      {
        q: "Do I need a permit?",
        a: "Almost always. Nearly every jurisdiction in Central Virginia requires a permit for exterior signs. Todd coordinates the permit process with the Superior Signs team, including any drawings or engineering the city requests.",
      },
      {
        q: "How long does a typical commercial sign project take?",
        a: "It varies. A simple wall sign can move in a few weeks; a monument sign, or anything involving landlord approval plus permitting, often takes longer. Todd gives you a realistic timeline early so you can plan around it.",
      },
      {
        q: "Can you help if I've already gotten a quote from another company?",
        a: "Yes. Many customers reach out to have a second set of eyes on a proposal before they sign. Todd can review what's included, flag anything that looks off, and answer questions in plain language.",
      },
    ],
    relatedProjectSlugs: ["commonwealth-equipment", "bee-charmed", "mosaic-group"],
    bottomCta: {
      headline: "Ready to start your sign project?",
      body: "Send Todd a note or give him a call. He usually responds within one business day.",
    },
    breadcrumbLabel: "Commercial Signs Richmond, VA",
  },

  "storefront-signs-richmond-va": {
    path: "/storefront-signs-richmond-va",
    seoTitle: "Storefront Signs Richmond VA | Channel Letters & Building Signs",
    seoDescription:
      "Storefront signage for Richmond, VA businesses — channel letters, illuminated cabinets, dimensional letters, permits, landlord criteria, and installation. Talk with Todd Friedman.",
    eyebrow: "Storefront Signs · Richmond, VA",
    h1: "Storefront Signs in Richmond, Virginia",
    intro: [
      "A storefront sign is usually the first sign a business needs and the most important one on the property. It has to survive weather, meet landlord and city rules, communicate the brand, and stay legible from the road.",
      "Todd is the local customer point of contact for Richmond-area storefront sign projects, coordinating work through the Superior Signs team — from options review through landlord approvals to a finished, permitted, installed sign.",
    ],
    sections: [
      {
        heading: "Channel letters",
        kind: "paragraphs",
        paragraphs: [
          "Channel letters are three-dimensional letters, typically internally illuminated with LEDs. They can be front-lit (translucent faces), halo-lit (light behind opaque faces, glowing on the wall), or reverse/combo-lit.",
          "Individual letters mount straight to the wall. This is the cleanest look, but it usually requires a good mounting surface and hidden electrical access.",
        ],
      },
      {
        heading: "Raceway-mounted letters",
        kind: "paragraphs",
        paragraphs: [
          "A raceway is a metal enclosure the letters mount onto. It houses the wiring and transformer, and it lets you install a lit sign on a wall without exposing electrical work behind the letters.",
          "Raceway mounts are often required by landlords or preferred when the wall material makes individual mounts difficult.",
        ],
      },
      {
        heading: "Illuminated cabinet signs",
        kind: "paragraphs",
        paragraphs: [
          "Cabinet signs are a single lightbox with a printed or translucent face. They can be simpler to permit and less expensive than channel letters, but they don't have the same depth or presence.",
        ],
      },
      {
        heading: "Dimensional (non-illuminated) letters",
        kind: "paragraphs",
        paragraphs: [
          "Dimensional letters are three-dimensional letters without built-in lighting. They can be mounted flush to the wall or with standoffs, and they can be lit externally with gooseneck lamps if the site allows.",
          "Dimensional letters can be a good fit when illumination isn't allowed, isn't needed, or when the budget is a factor.",
        ],
      },
      {
        heading: "Landlord sign criteria",
        kind: "paragraphs",
        paragraphs: [
          "Most commercial properties have a sign criteria document that spells out what kind of sign is allowed, where it can go, how big it can be, whether it can be illuminated, and how it has to be mounted.",
          "Getting this document early — before a design is commissioned — saves rework later. Todd asks for it up front on every project.",
        ],
      },
      {
        heading: "Electrical requirements",
        kind: "list",
        items: [
          "Landlord-provided electrical stub near the sign location, or coordination with a licensed electrician",
          "Dedicated circuit sized for the sign's LED load",
          "Time clock or photocell for on/off scheduling, when required by landlord or city",
          "GFCI protection where the electrical code requires it",
        ],
      },
      {
        heading: "Sign permits",
        kind: "paragraphs",
        paragraphs: [
          "Almost every jurisdiction in Central Virginia requires a permit for a storefront sign. The permit application typically includes scaled drawings, elevation photos, engineering (if attaching to structure), and landlord authorization.",
          "Todd coordinates the permit process with the Superior Signs team — including preparation of the application, communication with the city, and any revisions the reviewer asks for.",
        ],
      },
      {
        heading: "Site surveys",
        kind: "paragraphs",
        paragraphs: [
          "A survey is the first step. It answers the practical questions: How is the wall built? Is there existing electrical? Is there a raceway or an old sign to remove? What's the fastest legal way to get the new sign up?",
          "Todd coordinates site surveys with the Superior Signs team and communicates the findings back to you.",
        ],
      },
      {
        heading: "Typical project sequence",
        kind: "list",
        items: [
          "Initial call — what do you need, what's the deadline, what's the property",
          "Site survey and landlord criteria review",
          "Concept and layout discussion",
          "Drawings",
          "Landlord approval",
          "Permit application",
          "Fabrication",
          "Installation and final inspection",
        ],
      },
      {
        heading: "Cost factors",
        kind: "list",
        items: [
          "Sign size and letter height",
          "Illumination type (front-lit, halo-lit, non-illuminated)",
          "Mounting method (individual letters vs. raceway)",
          "Wall material and access (lift, scaffold, building height)",
          "Landlord requirements",
          "Permit and engineering fees",
          "Removal of any existing signage",
        ],
      },
    ],
    faqs: [
      {
        q: "How tall should the letters be?",
        a: "It depends on set-back from the road and viewing angle. Letters that read well from a parking lot are too small to read from a highway; letters sized for the highway can look out of proportion up close. Todd walks through this during the initial consultation.",
      },
      {
        q: "Do I have to use channel letters?",
        a: "No. Dimensional letters, cabinet signs, and vinyl-and-hardware alternatives all have a place. The right choice depends on the property, the brand, and what the landlord and city will approve.",
      },
      {
        q: "What if my landlord hasn't sent me the sign criteria?",
        a: "That's normal. Todd will help you request it and, if needed, work with the landlord's property manager directly.",
      },
    ],
    relatedProjectSlugs: ["bee-charmed", "commonwealth-equipment", "aspire-church"],
    bottomCta: {
      headline: "Ready to talk through a storefront sign?",
      body: "Send Todd a note or give him a call. He usually responds within one business day.",
    },
    breadcrumbLabel: "Storefront Signs Richmond, VA",
  },

  "monument-signs-richmond-va": {
    path: "/monument-signs-richmond-va",
    seoTitle: "Monument Signs Richmond VA | Design, Permitting & Installation",
    seoDescription:
      "Monument signs for Richmond, VA businesses — new monuments, refurbishments, tenant panels, electronic message centers, permits and installation. Talk with Todd Friedman.",
    eyebrow: "Monument Signs · Richmond, VA",
    h1: "Monument Signs in Richmond, Virginia",
    intro: [
      "A monument sign sits at ground level, usually near the road, and is often the first sign drivers see. Because it involves masonry, setbacks, utilities and permitting, it takes more coordination than a wall sign — and more up-front thinking.",
      "Todd Friedman is the local customer point of contact for Richmond-area properties planning, permitting and installing new monument signs, refurbishing existing ones, and updating tenant panels — coordinating the work through the Superior Signs team.",
    ],
    sections: [
      {
        heading: "New monument signs",
        kind: "paragraphs",
        paragraphs: [
          "A new monument sign starts with location — where can the sign legally sit given setbacks, easements, and utility lines. From there: design, engineering, permitting, foundation, masonry, cabinet or letters, and electrical.",
          "The goal is a sign that reads clearly from the road, complies with local code, and doesn't sit awkwardly on the property.",
        ],
      },
      {
        heading: "Refacing and refurbishing existing monuments",
        kind: "paragraphs",
        paragraphs: [
          "An older monument can often be updated instead of replaced. Common approaches include refacing the cabinet, replacing faded LEDs, cleaning and repainting the masonry, and updating tenant panels for a multi-tenant sign.",
          "Refurbishing is usually faster, less expensive, and easier to permit than a full replacement.",
        ],
      },
      {
        heading: "Tenant panels",
        kind: "paragraphs",
        paragraphs: [
          "Multi-tenant monuments use interchangeable panels for each business. Todd coordinates individual tenant-panel updates with the Superior Signs team as tenants change — matching the existing color, size and mounting style so the panel fits cleanly into the existing cabinet.",
        ],
      },
      {
        heading: "Electronic message centers (EMC)",
        kind: "paragraphs",
        paragraphs: [
          "An EMC is a digital display integrated into a monument sign. They give you flexibility (dayparting, messages, event promotion) but come with their own set of rules — many jurisdictions restrict brightness, message-change frequency, and animation.",
          "Todd looks into whether an EMC is allowed at your property before drawings begin.",
        ],
      },
      {
        heading: "Property lines, setbacks and utilities",
        kind: "list",
        items: [
          "Setback distance from the road, sidewalk and property line",
          "Underground utility locates before any excavation",
          "Easements — power, gas, water, telecommunications",
          "Line of sight for traffic — the sign can't block a driver's view of a driveway or intersection",
          "Landlord and management-company approval, when applicable",
        ],
      },
      {
        heading: "Masonry and structural considerations",
        kind: "paragraphs",
        paragraphs: [
          "Monument signs need a foundation sized for the sign, the wind load and the soil conditions. Larger monuments often require engineered drawings.",
          "Masonry (brick, stone, stucco) is usually the visible base. It's chosen to match the property's architecture and to hold up in the local climate.",
        ],
      },
      {
        heading: "Permitting",
        kind: "paragraphs",
        paragraphs: [
          "Monument permits typically involve a scaled site plan, elevation drawings, structural engineering, and — for lit signs — an electrical plan. Todd coordinates the application with the Superior Signs team and works with the jurisdiction until it's approved.",
        ],
      },
      {
        heading: "Cost factors",
        kind: "list",
        items: [
          "Overall size and structural design",
          "Masonry materials",
          "Number of tenant panels",
          "Illumination (internal, halo, or external)",
          "Electronic message center, if included",
          "Foundation and site work",
          "Engineering and permit fees",
        ],
      },
    ],
    faqs: [
      {
        q: "Can we replace an old monument with a new one on the same footprint?",
        a: "Sometimes yes, sometimes not. Existing structures are often grandfathered to old codes; a replacement may trigger current setback or size rules. Todd looks into this before drawings begin.",
      },
      {
        q: "How long does a monument-sign project take?",
        a: "Most projects run several weeks to several months depending on permitting, engineering, weather, and how quickly the landlord and utilities respond. Todd gives you a realistic timeline up front.",
      },
      {
        q: "Are electronic message centers allowed everywhere?",
        a: "No. Many Richmond-area jurisdictions restrict EMCs, especially near residential zones. Todd looks into allowability early to avoid a permit denial.",
      },
    ],
    relatedProjectSlugs: ["mosaic-group", "granger-genetics", "cross-keys"],
    bottomCta: {
      headline: "Ready to plan a monument sign?",
      body: "Send Todd a note or give him a call. He usually responds within one business day.",
    },
    breadcrumbLabel: "Monument Signs Richmond, VA",
  },

  "sign-installation-richmond-va": {
    path: "/sign-installation-richmond-va",
    seoTitle: "Sign Installation Richmond VA | Surveys, Permits & Receive-and-Install",
    seoDescription:
      "Local Richmond, VA sign installation, surveys, permitting and receive-and-install support for national sign companies, project managers, contractors and multi-location brands.",
    eyebrow: "Installation & Local Support · Richmond, VA",
    h1: "Sign Installation and Local Project Support in Richmond",
    intro: [
      "If you're a national sign company, a project manager coordinating a rollout, a general contractor scheduling a build-out, or a multi-location brand needing eyes and hands on the ground in Richmond — Todd Friedman is a straightforward local point of contact.",
      "Todd coordinates the local pieces you can't do from another state — site surveys, permit filings, receive-and-install, communication with landlords and electricians, and final-inspection close-out — with the experienced Superior Signs team. Fabrication can be provided through Superior Signs or by the national sign company; the local work in Richmond gets scheduled and handled on the ground.",
    ],
    sections: [
      {
        heading: "Site surveys",
        kind: "paragraphs",
        paragraphs: [
          "Todd coordinates on-site surveys on your behalf with the Superior Signs team and communicates the findings back to you: wall condition, dimensions, existing electrical, mounting surface, access requirements, and photos with measurements. Reports come back in the format your project managers expect.",
        ],
      },
      {
        heading: "Permit procurement",
        kind: "paragraphs",
        paragraphs: [
          "Todd coordinates the local permit application with the Superior Signs team — communicating with the city or county, submitting scaled drawings and landlord authorization, and following up on revisions. He knows what each jurisdiction typically wants to see, which reduces rounds of back-and-forth.",
        ],
      },
      {
        heading: "Receive-and-install",
        kind: "paragraphs",
        paragraphs: [
          "You ship the sign; Todd serves as the local point of contact for receiving, staging and installation, coordinated with the Superior Signs team, to your specifications and the manufacturer's mounting instructions.",
        ],
      },
      {
        heading: "Removal and disposal",
        kind: "paragraphs",
        paragraphs: [
          "Old signage removal, wall patching and painting to a reasonable finish, and disposal of hardware are coordinated through the Superior Signs team. Photos are provided for your records.",
        ],
      },
      {
        heading: "Electrical coordination",
        kind: "paragraphs",
        paragraphs: [
          "For lit signs, Todd coordinates with a local licensed electrician for connections, disconnects, dedicated circuits and any code-required upgrades — so your install crew isn't sitting on a job waiting for power.",
        ],
      },
      {
        heading: "Completion photos and final inspections",
        kind: "list",
        items: [
          "Pre-install site conditions and existing signage",
          "Staged materials on site",
          "Mid-install progress",
          "Final installation, day and (for lit signs) night",
          "Punch-list follow-through until the local inspector signs off",
        ],
      },
      {
        heading: "Service territory",
        kind: "paragraphs",
        paragraphs: [
          "Todd covers Richmond and Central Virginia. That includes Henrico, Chesterfield, Midlothian, Glen Allen, Short Pump, Hanover, Ashland, Mechanicsville and surrounding jurisdictions.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you carry insurance suitable for national-brand work?",
        a: "Installation is coordinated with the experienced Superior Signs team, which regularly supports commercial and national-account signage projects. Certificates of insurance can be provided to your risk manager on request.",
      },
      {
        q: "What information do you need to quote a receive-and-install?",
        a: "The site address, photos, sign specifications (size, weight, mounting method), electrical needs, any special access requirements, and your ideal install window. Todd can also coordinate a survey first if that helps.",
      },
      {
        q: "Can you handle multi-site rollouts?",
        a: "Within Central Virginia, yes — Todd is a local resource who partners with your program manager. He is not a replacement for a national vendor covering multiple states.",
      },
    ],
    relatedProjectSlugs: ["target", "loehr-lightning", "granger-genetics"],
    bottomCta: {
      headline: "Have a project in Richmond?",
      body: "Send Todd the details or give him a call. He usually responds within one business day.",
    },
    breadcrumbLabel: "Sign Installation Richmond, VA",
  },

  "new-business-signage-richmond-va": {
    path: "/new-business-signage-richmond-va",
    seoTitle: "New Business Signage Richmond VA | From Lease to Installation",
    seoDescription:
      "Opening a business in Richmond, VA? Todd Friedman walks new owners through the signage process — landlord criteria, surveys, permits, drawings, fabrication and install.",
    eyebrow: "Opening a Business · Richmond, VA",
    h1: "Opening a Business in Richmond? Start Your Sign Project Here.",
    intro: [
      "Signage is one of the last things most new business owners think about — and one of the last things a landlord will approve. Starting the process early makes the difference between opening with a real sign on the building and opening with a paper banner in the window.",
      "Todd Friedman helps Richmond-area business owners work through the sign process from lease signing to installation, one step at a time.",
    ],
    sections: [
      {
        heading: "1. Get the landlord's sign criteria",
        kind: "paragraphs",
        paragraphs: [
          "Every commercial lease with a sign has a sign-criteria document behind it. Get a copy in writing before you design anything. It tells you the allowed size, mounting method, illumination type, colors, and approval process.",
        ],
      },
      {
        heading: "2. Confirm the allowable sign area",
        kind: "paragraphs",
        paragraphs: [
          "Landlord rules are one layer. City and county sign codes are another. Both apply — the sign has to satisfy the stricter of the two. Todd checks both before any drawings are commissioned.",
        ],
      },
      {
        heading: "3. Survey the property",
        kind: "paragraphs",
        paragraphs: [
          "A site survey confirms wall condition, dimensions, existing electrical, any existing hardware to remove, and mounting method. It's the difference between a design that installs cleanly and a design that has to be reworked at fabrication.",
        ],
      },
      {
        heading: "4. Develop drawings",
        kind: "paragraphs",
        paragraphs: [
          "Scaled drawings show the sign on the wall in context — size, position, materials, colors, illumination. They're what the landlord and city need in order to approve.",
        ],
      },
      {
        heading: "5. Secure landlord approval",
        kind: "paragraphs",
        paragraphs: [
          "The landlord signs off on the drawings before the permit is submitted. This step protects you from paying to fabricate a sign the landlord later refuses.",
        ],
      },
      {
        heading: "6. Apply for permits",
        kind: "paragraphs",
        paragraphs: [
          "Permit applications typically include the drawings, elevation photos, landlord authorization letter, and — for lit signs — an electrical plan. Todd coordinates the application with the Superior Signs team and any revisions the city requests.",
        ],
      },
      {
        heading: "7. Fabricate",
        kind: "paragraphs",
        paragraphs: [
          "With permit in hand, the sign goes into fabrication. Lead times vary by sign type. Todd sets expectations up front so your opening date and your sign install line up.",
        ],
      },
      {
        heading: "8. Install",
        kind: "paragraphs",
        paragraphs: [
          "Install day is the payoff. Todd coordinates with the property manager, the electrician (if needed) and the install crew so the sign goes up cleanly and the inspection happens on schedule.",
        ],
      },
      {
        heading: "Start early enough to avoid opening without signage",
        kind: "paragraphs",
        paragraphs: [
          "The most common mistake new business owners make is starting the sign conversation two weeks before opening. Between landlord approval, permitting and fabrication, most sign projects take longer than most people expect.",
          "The best time to start talking about your sign is the day you sign the lease.",
        ],
      },
      {
        heading: "A short checklist",
        kind: "list",
        items: [
          "Request landlord sign criteria in writing",
          "Confirm allowable sign area with landlord and city",
          "Schedule a site survey",
          "Approve drawings",
          "Landlord approval letter",
          "Permit application submitted",
          "Fabrication scheduled to align with your opening date",
          "Coordinate install with landlord, electrician and inspector",
          "Post-install: final photos and inspection close-out",
        ],
      },
    ],
    faqs: [
      {
        q: "How early should I start?",
        a: "The day you sign the lease. Most sign projects take longer than new business owners expect — often 6 to 12 weeks by the time landlord approval, permitting, fabrication and installation are all counted.",
      },
      {
        q: "What if my landlord is slow to respond?",
        a: "That's common. Todd will help you follow up in writing and, if needed, work directly with the property manager to keep things moving.",
      },
      {
        q: "Can I open with just a temporary sign?",
        a: "Sometimes. A banner or window vinyl can bridge the gap while permanent signage is in permitting or fabrication. Most jurisdictions have rules on how long a temporary sign can stay up.",
      },
    ],
    relatedProjectSlugs: ["bee-charmed", "commonwealth-equipment", "aspire-church"],
    bottomCta: {
      headline: "Signed a lease? Let's talk.",
      body: "Send Todd a note or give him a call. He usually responds within one business day.",
    },
    breadcrumbLabel: "New Business Signage Richmond, VA",
  },
};
